import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useFavicon from '../hooks/useFavicon';
import { SettingsAPI, ConfigurationAPI } from '../apis';
import { getFromCache, saveToCache } from '../utils/indexedDBCache';
// Applied when a key is absent or empty in the DB — the DB value always takes
// precedence. Reads process.env at call time so Jest tests can set env vars
// without reloading the module (CRA replaces these with literals at build time
// so the call-time vs load-time distinction only matters in tests).
const applyEnvFallbacks = (configMap) => {
    const showAppMenu = process.env.REACT_APP_SHOW_APPLICATION_MENU;
    const showProductPage = process.env.REACT_APP_SHOW_PRODUCT_SELECTION_PAGE;
    const fallbacks = {
        RBAC_UI_COOKIE_URL: process.env.REACT_APP_COOKIE_URL,
        RBAC_UI_PRIVACY_URL: process.env.REACT_APP_PRIVACY_URL,
        RBAC_UI_BEACON_ANALYTICS_URL: process.env.REACT_APP_BEACON_ANALYTICS_URL,
        RBAC_UI_COOKIE_SRC: process.env.REACT_APP_COOKIE_SRC,
        RBAC_UI_DATA_DOMAIN_ID: process.env.REACT_APP_DATA_DOMAIN_ID,
        RBAC_UI_APP_TITLE: process.env.REACT_APP_TITLE,
        RBAC_UI_SHOW_APPLICATION_MENU: showAppMenu === undefined ? undefined : showAppMenu === 'true',
        RBAC_UI_SHOW_PRODUCT_SELECTION_PAGE: showProductPage === undefined ? undefined : showProductPage === 'true',
        RBAC_UI_IDP_USER_LOOKUP_API_ENABLED: process.env.REACT_APP_IDP_USER_LOOKUP_API_ENABLED,
        RBAC_UI_USER_LOOKUP_EMAIL_DOMAIN: process.env.REACT_APP_USER_LOOKUP_EMAIL_DOMAIN,
        RBAC_UI_VAPID_PUBLIC_KEY: process.env.REACT_APP_VAPID_PUBLIC_KEY
    };
    const result = { ...configMap };
    for (const [key, fallback] of Object.entries(fallbacks)) {
        if ((result[key] === undefined || result[key] === '') && fallback !== undefined) {
            result[key] = fallback;
        }
    }
    return result;
};
export const ConfigContext = React.createContext({});
export const ConfigProvider = ({ children }) => {
    const initialConfigState = {
        selectedProduct: '',
        settings: {
            resourceType: '',
            id: 0,
            workspace_name: '',
            page_image: '',
            background_color: '',
            button_color: '',
            button_text_color: '',
            workspace_name_color: '',
            header_background_color: '',
            header_logo_image: '',
            industry_name: '',
            include_badge: false,
            welcome_message_color: '',
            show_logo: false,
            show_workspace_name: true,
            show_deloitte_logo: false
        }
    };
    const [configState, setConfigState] = useState(initialConfigState);
    // Always use default favicon - don't change based on header logo
    useFavicon('', false);
    const contextValue = useMemo(() => ({ configState, setConfigState }), [configState]); // value is cached by useMemo
    return (_jsx(ConfigContext.Provider, { value: contextValue, children: children }));
};
// Ongoing request trackers to prevent duplicate API calls
let ongoingSettingsRequest = null;
let ongoingPageImageRequest = null;
let ongoingHeaderImageRequest = null;
let ongoingUxConfigRequest = null;
// Hook to get settings with IndexedDB caching (persists until logout)
export const useSettingsData = () => {
    const [cachedSettings, setCachedSettings] = useState(null);
    const CACHE_KEY = 'settings_data';
    const cachedSettingsRef = useRef(cachedSettings);
    useEffect(() => {
        cachedSettingsRef.current = cachedSettings;
    }, [cachedSettings]);
    const getSettings = useCallback(async () => {
        // 1. Check in-memory cache first (fastest)
        // Check for null explicitly for consistency
        if (cachedSettingsRef.current !== null) {
            return cachedSettingsRef.current;
        }
        // 2. Check IndexedDB cache (persists until logout, survives page reloads)
        try {
            const cachedData = await getFromCache(CACHE_KEY);
            // Check for null explicitly
            if (cachedData !== null) {
                setCachedSettings(cachedData);
                return cachedData;
            }
        }
        catch (error) {
            console.warn('Failed to read settings from IndexedDB cache:', error);
        }
        // 3. Return ongoing request if exists (prevent duplicate API calls)
        if (ongoingSettingsRequest) {
            return ongoingSettingsRequest;
        }
        // 4. Fetch from API and cache the result
        const requestPromise = (async () => {
            try {
                const settings = await SettingsAPI.getSettings();
                // Update in-memory cache
                setCachedSettings(settings);
                // Update IndexedDB cache
                try {
                    await saveToCache(CACHE_KEY, settings);
                }
                catch (error) {
                    console.warn('Failed to cache settings to IndexedDB:', error);
                }
                return settings;
            }
            catch (error) {
                ongoingSettingsRequest = null;
                throw error;
            }
            finally {
                ongoingSettingsRequest = null;
            }
        })();
        // Store promise to prevent duplicates
        ongoingSettingsRequest = requestPromise;
        return requestPromise;
    }, []);
    return getSettings;
};
// Hook to get page image with IndexedDB caching (persists until logout)
export const usePageImage = () => {
    const [cachedImage, setCachedImage] = useState(null);
    const CACHE_KEY = 'page_image_data';
    const cachedImageRef = useRef(cachedImage);
    useEffect(() => {
        cachedImageRef.current = cachedImage;
    }, [cachedImage]);
    const getPageImage = useCallback(async () => {
        // 1. Check in-memory cache first (fastest)
        // Check for null explicitly - empty string is a valid cached value
        if (cachedImageRef.current !== null) {
            return cachedImageRef.current;
        }
        // 2. Check IndexedDB cache (persists until logout, survives page reloads)
        try {
            const cachedData = await getFromCache(CACHE_KEY);
            // Check for null explicitly - empty string means no image configured
            if (cachedData !== null) {
                setCachedImage(cachedData);
                return cachedData;
            }
        }
        catch (error) {
            console.warn('Failed to read page image from IndexedDB cache:', error);
        }
        // 3. Return ongoing request if exists (prevent duplicate API calls)
        if (ongoingPageImageRequest) {
            return ongoingPageImageRequest;
        }
        // 4. Fetch from API and cache the result
        const requestPromise = (async () => {
            try {
                const image = await SettingsAPI.getSettingsImage('theme', 'image');
                // Update in-memory cache
                setCachedImage(image);
                // Update IndexedDB cache
                try {
                    await saveToCache(CACHE_KEY, image);
                }
                catch (error) {
                    console.warn('Failed to cache page image to IndexedDB:', error);
                }
                return image;
            }
            catch (error) {
                ongoingPageImageRequest = null;
                throw error;
            }
            finally {
                ongoingPageImageRequest = null;
            }
        })();
        // Store promise to prevent duplicates
        ongoingPageImageRequest = requestPromise;
        return requestPromise;
    }, []);
    return getPageImage;
};
// Hook to get header image with IndexedDB caching (persists until logout)
export const useHeaderImage = () => {
    const [cachedImage, setCachedImage] = useState(null);
    const CACHE_KEY = 'header_image_data';
    const cachedImageRef = useRef(cachedImage);
    useEffect(() => {
        cachedImageRef.current = cachedImage;
    }, [cachedImage]);
    const getHeaderImage = useCallback(async () => {
        // 1. Check in-memory cache first (fastest)
        // Check for null explicitly - empty string is a valid cached value
        if (cachedImageRef.current !== null) {
            return cachedImageRef.current;
        }
        // 2. Check IndexedDB cache (persists until logout, survives page reloads)
        try {
            const cachedData = await getFromCache(CACHE_KEY);
            // Check for null explicitly - empty string means no image configured
            if (cachedData !== null) {
                setCachedImage(cachedData);
                return cachedData;
            }
        }
        catch (error) {
            console.warn('Failed to read header image from IndexedDB cache:', error);
        }
        // 3. Return ongoing request if exists (prevent duplicate API calls)
        if (ongoingHeaderImageRequest) {
            return ongoingHeaderImageRequest;
        }
        // 4. Fetch from API and cache the result
        const requestPromise = (async () => {
            try {
                const image = await SettingsAPI.getSettingsImage('theme', 'logo');
                // Update in-memory cache
                setCachedImage(image);
                // Update IndexedDB cache
                try {
                    await saveToCache(CACHE_KEY, image);
                }
                catch (error) {
                    console.warn('Failed to cache header image to IndexedDB:', error);
                }
                return image;
            }
            catch (error) {
                ongoingHeaderImageRequest = null;
                throw error;
            }
            finally {
                ongoingHeaderImageRequest = null;
            }
        })();
        // Store promise to prevent duplicates
        ongoingHeaderImageRequest = requestPromise;
        return requestPromise;
    }, []);
    return getHeaderImage;
};
// Hook to get user experience config with IndexedDB caching (persists until logout)
export const useUserExperienceConfig = () => {
    const [cachedConfig, setCachedConfig] = useState(null);
    const CACHE_KEY = 'ux_config_data';
    const cachedConfigRef = useRef(cachedConfig);
    useEffect(() => {
        cachedConfigRef.current = cachedConfig;
    }, [cachedConfig]);
    const getConfig = useCallback(async () => {
        // 1. Check in-memory cache first (fastest)
        if (cachedConfigRef.current !== null) {
            return cachedConfigRef.current;
        }
        // 2. Check IndexedDB cache (persists until logout, survives page reloads)
        try {
            const cachedData = await getFromCache(CACHE_KEY);
            // Check for null explicitly
            if (cachedData !== null) {
                setCachedConfig(cachedData);
                return cachedData;
            }
        }
        catch (error) {
            console.warn('Failed to read UX config from IndexedDB cache:', error);
        }
        // 3. Return ongoing request if exists (prevent duplicate API calls)
        if (ongoingUxConfigRequest) {
            return ongoingUxConfigRequest;
        }
        // 4. Fetch from API and cache the result
        const requestPromise = (async () => {
            try {
                const bundle = await ConfigurationAPI.getPublicUserExperienceConfig();
                const config = bundle.resource?.[0];
                const rawConfigMap = {};
                for (const item of config?.items || []) {
                    rawConfigMap[item.key] = item.value;
                }
                const configMap = applyEnvFallbacks(rawConfigMap);
                // Update in-memory cache
                setCachedConfig(configMap);
                // Update IndexedDB cache
                try {
                    await saveToCache(CACHE_KEY, configMap);
                }
                catch (error) {
                    console.warn('Failed to cache UX config to IndexedDB:', error);
                }
                return configMap;
            }
            catch (error) {
                ongoingUxConfigRequest = null;
                throw error;
            }
            finally {
                ongoingUxConfigRequest = null;
            }
        })();
        // Store promise to prevent duplicates
        ongoingUxConfigRequest = requestPromise;
        return requestPromise;
    }, []);
    return getConfig;
};
